import {formatDateFns} from '@shared/helpers/date'
import {isNil} from '@shared/helpers/lang'

import {getArrivalGuaranteeValidate} from '@web/actions/deliveryInfo'
import {
    ProductType,
    ProductTypeType,
    SellerExceptionalCategory,
    SellerExceptionalCategoryType,
} from '@web/core/constants'
import {getDeliveryTag, isHopeSellerGuarantee as isHopeSellerGuaranteeByUtil} from '@web/helpers/policies'
import makeSlice from '@web/slices/orderSheetProducts/sliceMaker'

import {fetchDeliveryFee, fetchSubscription} from '../../actions'

function convertDate(date: string) {
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`
}

function convertTime(date: Maybe<string>) {
    if (date) {
        return `${date.slice(0, 2)}:${date.slice(2, 4)}`
    }
    return date
}

const SellerExceptionCateogryMap: Record<SellerExceptionalCategoryType, ProductTypeType> = {
    [SellerExceptionalCategory.NONE]: ProductType.NORMAL,
    [SellerExceptionalCategory.BOOK]: ProductType.BOOK,
    [SellerExceptionalCategory.ALCOHOL]: ProductType.ALCOHOL,
    [SellerExceptionalCategory.TRADITIONAL_ALCOHOL]: ProductType.TRADITIONAL_ALCOHOL,
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscription.fulfilled, (_, {payload: {subscriptionViewResult}}) => {
                const {products} = subscriptionViewResult

                // 도착보장 상품의 경우 오늘도착/내일도착 등 태그결정을 위해 주문서조회시각을 만들어서 넘겨줌
                const currentDateTime = new Date().toISOString()

                return products.map((product) => {
                    return {
                        id: product.orderSheetProductId,
                        name: product.productName,
                        url: product.productUrl,
                        imageUrl: product.productImageUrl,
                        type: SellerExceptionCateogryMap[product.sellerExceptionalCategoryType],

                        unitPrice: product.salePrice,
                        naverBenefit: product.naverBenefit,
                        customOrderDetailType: product.customOrderDetailType,
                        preOrder: product.preOrder,
                        merchantInfo: {
                            merchantProductId: product.merchantProductId,
                            merchantNo: product.merchantNo,
                            merchantChannelName: product.merchantChannelName,
                            merchantChannelServiceType: product.merchantChannelServiceType,
                        },
                        deliveryInfo: {
                            deliveryGroupKey: product.deliveryGroupKey,
                            deliveryAttributeType: product.deliveryAttributeType,
                            overseasDelivery: product.overseasDelivery,
                            branch: product.branchDeliveryView
                                ? {
                                      deliverySlotId: product.branchDeliveryView.deliverySlotId,
                                      branchDeliveryDate: convertDate(product.branchDeliveryView.branchDeliveryDate),
                                      branchDeliveryStartTime: convertTime(
                                          product.branchDeliveryView.branchDeliveryStartTime,
                                      ),
                                      branchDeliveryEndTime: convertTime(
                                          product.branchDeliveryView.branchDeliveryEndTime,
                                      ),
                                      deliverySlotUsed: product.branchDeliveryView.deliverySlotUsed,
                                      branchDeliveryPreparationMinutes:
                                          product.branchDeliveryView.branchDeliveryPreparationMinutes ?? 0,
                                      branchDeliveryDurationMinutes: 0,
                                  }
                                : null,
                            todayDispatch:
                                !isNil(product.todayDispatch) &&
                                (product.todayDispatchBasisTime ||
                                    product.todayDispatchExpectedDateTime ||
                                    product.todayDispatchNotAppliedReason)
                                    ? {
                                          shouldTodayDispatch: product.todayDispatch,
                                          basisTime: convertTime(product.todayDispatchBasisTime),
                                          expectedDate: product.todayDispatchExpectedDateTime,
                                          notAppliedReason: product.todayDispatchNotAppliedReason,
                                      }
                                    : null,
                            hopeDelivery: product.hopeDelivery
                                ? {
                                      region: product.hopeDelivery.region,
                                      additionalFee: product.hopeDelivery.additionalFee,
                                      hopeDeliveryDate: product.hopeDelivery.hopeDeliveryDate,
                                      hopeDeliveryTime: product.hopeDelivery.hopeDeliveryTime
                                          ? `${product.hopeDelivery.hopeDeliveryTime.slice(0, 5)}`
                                          : null,
                                  }
                                : null,
                            deliveryPrediction: product.deliveryPrediction && {
                                ...product.deliveryPrediction,
                                best: true,
                                visible: true,
                            },
                            deliveryTagType: getDeliveryTag({
                                arrivalDate: product.arrivalGuarantee?.arrivalGuaranteeDateTime,
                                ...(product.deliveryAttributeType && {
                                    deliveryAttributeType: product.deliveryAttributeType,
                                }),
                                ...(product.arrivalGuarantee?.deliveryTagType && {
                                    deliveryTagType: product.arrivalGuarantee?.deliveryTagType,
                                }),
                            }),
                        },
                        items: product.items.map((item) => {
                            return {
                                orderSheetItemId: item.orderSheetItemId,
                                productClassType: item.productClassType,
                                itemNo: item.itemNo,
                                price: item.price,
                                quantity: item.quantity,
                                orderAmount: item.orderAmount,
                                optionContents: item.optionContents,
                                branchId: item.branchId,
                                branchBenefitType: item.branchBenefitType,
                                branchBenefitValue: item.branchBenefitValue,
                                branchBenefitApplyValue: item.benefitApplyValue,
                                elements: item.elements,
                            }
                        }),
                        gift: product.gift,
                        cupDeposit: null,
                        ecouponBarcodeConnect: product.ecouponBarcodeConnect,
                        arrivalGuaranteeDateTime: product.arrivalGuarantee?.arrivalGuaranteeDateTime ?? null,
                        arrivalGuaranteeCheckedDateTime: product.arrivalGuarantee ? currentDateTime : null,
                        rmid: product.arrivalGuarantee?.rmid ?? null,
                        outboundLocationId: product.arrivalGuarantee?.outboundLocationId ?? null,
                        quantity: null,
                        orderAmount: null,
                        installationProduct: null,
                    }
                })
            })
            .addCase(
                fetchDeliveryFee.fulfilled,
                (state, {payload: {updatedArrivalGuaranteesByOrderSheetProductId}}) => {
                    const arrivalGuaranteeInfoChanged = !!updatedArrivalGuaranteesByOrderSheetProductId

                    // 도착보장 상품의 경우 오늘도착/내일도착 등 태그결정을 위해 주문서조회시각을 만들어서 넘겨줌
                    const currentDateTime = new Date().toISOString()

                    if (arrivalGuaranteeInfoChanged) {
                        return state.map((product) => {
                            const updatedArrivalGuarantee =
                                updatedArrivalGuaranteesByOrderSheetProductId[product.id] ?? null
                            const {arrivalGuaranteeDateTime, rmid, outboundLocationId, deliveryTagType} =
                                updatedArrivalGuarantee ?? {
                                    arrivalGuaranteeDateTime: product.arrivalGuaranteeDateTime,
                                    rmid: product.rmid,
                                    outboundLocationId: product.outboundLocationId,
                                    deliveryTagType: product.deliveryInfo.deliveryTagType,
                                }
                            const hopeDeliveryDate = arrivalGuaranteeDateTime
                                ? formatDateFns(arrivalGuaranteeDateTime, 'yyyy-MM-dd')
                                : null
                            const hopeDeliveryTime = arrivalGuaranteeDateTime
                                ? formatDateFns(arrivalGuaranteeDateTime, 'HH:mm')
                                : null

                            return {
                                ...product,
                                arrivalGuaranteeDateTime,
                                arrivalGuaranteeCheckedDateTime: currentDateTime,
                                rmid,
                                outboundLocationId,
                                deliveryInfo: {
                                    ...product.deliveryInfo,
                                    ...(product.deliveryInfo.hopeDelivery &&
                                        isHopeSellerGuaranteeByUtil(product.deliveryInfo.deliveryAttributeType) && {
                                            hopeDelivery: {
                                                ...product.deliveryInfo.hopeDelivery,
                                                hopeDeliveryDate,
                                                hopeDeliveryTime,
                                            },
                                        }),
                                    deliveryTagType: getDeliveryTag({
                                        arrivalDate: arrivalGuaranteeDateTime ?? undefined,
                                        deliveryAttributeType: product.deliveryInfo.deliveryAttributeType ?? undefined,
                                        deliveryTagType: deliveryTagType ?? undefined,
                                    }),
                                },
                            }
                        })
                    }

                    return state
                },
            )
            .addCase(getArrivalGuaranteeValidate.fulfilled, (state, {payload}) => {
                if (payload.arrivalGuaranteeInfo?.isChanged) {
                    return state.map((product) => {
                        const updatedArrivalGuarantee =
                            payload.data.updatedArrivalGuaranteesByOrderSheetProductId[product.id] ?? null
                        const {
                            arrivalGuaranteeDateTime,
                            arrivalGuaranteeCheckedDateTime,
                            rmid,
                            outboundLocationId,
                            deliveryTagType,
                        } = updatedArrivalGuarantee ?? {
                            arrivalGuaranteeDateTime: product.arrivalGuaranteeDateTime,
                            arrivalGuaranteeCheckedDateTime: product.arrivalGuaranteeCheckedDateTime,
                            rmid: product.rmid,
                            outboundLocationId: product.outboundLocationId,
                            deliveryTagType: product.deliveryInfo.deliveryTagType,
                        }

                        return {
                            ...product,
                            arrivalGuaranteeDateTime,
                            arrivalGuaranteeCheckedDateTime,
                            rmid,
                            outboundLocationId,
                            deliveryInfo: {
                                ...product.deliveryInfo,
                                deliveryTagType: getDeliveryTag({
                                    arrivalDate: arrivalGuaranteeDateTime ?? undefined,
                                    deliveryAttributeType: product.deliveryInfo.deliveryAttributeType ?? undefined,
                                    deliveryTagType,
                                    orderDate: arrivalGuaranteeCheckedDateTime ?? undefined,
                                }),
                            },
                        }
                    })
                }
                return state
            })
    },
})

export default slice.reducer
export const actions = slice.actions
